// Sprint attachments-anthropic-parity Phase D (D8) — fullscreen image
// preview triggered by clicking a thumbnail inside an AttachmentChip.
//
// Closing affordances (Cursor-mirror):
//   - Esc key
//   - click on the backdrop (outside the image)
//   - swipe down on touch devices
//   - tap the close button (≥44×44 px tap target, per mobile-first)
//
// The lightbox is rendered behind a Radix Dialog Portal so it escapes
// the composer/bubble stacking context and overlays the whole viewport.
// Radix gives us Esc + backdrop-click + focus-trap + body-scroll-lock
// for free; we add the swipe-down touch handler on top.
//
// `ImageLightbox` is presentation-only. The parent owns `open` and the
// `src`/`alt` of the image to display; the child reports close intent.
//
// Tokens used: `bg-black/80` for the backdrop (matches every other modal
// surface in the repo — Dialog / AlertDialog / Sheet shadcn primitives,
// post-UI-10 single source of truth), `rounded-xl` (`radius-card`) for
// the image frame, `duration-motion-modal` (300ms, fade keyframe) for
// open/close, `touch-target` (44×44 px) for the close button. No raw
// values; the lightbox piggybacks on Radix Dialog primitives so Esc /
// backdrop-click / focus-trap / body-scroll-lock are free.

import { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Icon } from './atoms/Icon';
import { useTranslation } from '../i18n';

export interface ImageLightboxProps {
  open: boolean;
  src: string;
  alt: string;
  onClose: () => void;
}

// Touch distance (px) the user must drag down before we trigger close.
// 80 px is the Cursor / iOS-photos baseline; below this we keep the
// lightbox open (jitter tolerance for non-intentional drags).
const SWIPE_DOWN_THRESHOLD_PX = 80;

export function ImageLightbox({ open, src, alt, onClose }: ImageLightboxProps) {
  const { t } = useTranslation();
  const touchStartYRef = useRef<number | null>(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);

  // Reset the swipe state every time the lightbox opens, so a previous
  // drag attempt does not carry over into the next session.
  useEffect(() => {
    if (open) {
      touchStartYRef.current = null;
      setDragOffsetY(0);
    }
  }, [open]);

  function handleTouchStart(e: React.TouchEvent) {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartYRef.current = touch.clientY;
    setDragOffsetY(0);
  }

  function handleTouchMove(e: React.TouchEvent) {
    const startY = touchStartYRef.current;
    const touch = e.touches[0];
    if (startY === null || !touch) return;
    const delta = touch.clientY - startY;
    // Only track downward drags; an upward drag does nothing.
    if (delta > 0) setDragOffsetY(delta);
  }

  function handleTouchEnd() {
    if (dragOffsetY >= SWIPE_DOWN_THRESHOLD_PX) {
      onClose();
    } else {
      setDragOffsetY(0);
    }
    touchStartYRef.current = null;
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-motion-modal"
          data-testid="image-lightbox-overlay"
        />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 duration-motion-modal"
          aria-describedby={undefined}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          data-testid="image-lightbox"
        >
          <DialogPrimitive.Title className="sr-only">{alt}</DialogPrimitive.Title>

          {/* Click outside the image (i.e. on the padding ring) closes. */}
          <div
            className="absolute inset-0"
            onClick={onClose}
            data-testid="image-lightbox-backdrop"
            aria-hidden="true"
          />

          {/* The image itself. Click inside does NOT close (Cursor-mirror).
              The 90vh / 90vw caps are CSS viewport keywords, not design
              tokens — design-discipline allows viewport-arithmetic the same
              way it allows `calc(100vh - var(...))`. The drag transform is
              computed from live touch state; the snap-back transition uses
              the `motion-default` token via Tailwind. */}
          <img
            src={src}
            alt={alt}
            className="relative rounded-xl object-contain shadow-lg transition-[transform,opacity] duration-motion-default ease-out"
            style={{
              maxHeight: '90vh',
              maxWidth: '90vw',
              ...(dragOffsetY > 0
                ? {
                    transform: `translateY(${dragOffsetY}px)`,
                    opacity: Math.max(1 - dragOffsetY / 400, 0.3),
                  }
                : null),
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="image-lightbox-image"
            draggable={false}
          />

          <DialogPrimitive.Close
            className="absolute right-4 top-4 inline-flex h-touch-target w-touch-target items-center justify-center rounded-full bg-background/80 text-foreground hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors duration-motion-default"
            aria-label={t('composer.imageLightboxClose')}
            data-testid="image-lightbox-close"
          >
            <Icon name="X" size={20} aria-hidden="true" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
